import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, Globe, Youtube, Instagram, Facebook, Share2 } from "lucide-react";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from "./NewsTicker";
import SocialLinks, { SOCIAL_FIELDS } from "./SocialLinks";

const OTHERS_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Description" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "address", label: "Address" },
  { key: "fullAddress", label: "Full Address", type: "textarea", placeholder: "Enter complete address for Google Maps..." },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  { key: "socialMediaUrl", label: "Social Media URL", type: "url", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface OthersEntry {
  name: string;
  description: string;
  website: string;
  address: string;
  fullAddress?: string;
  image: string;
  socialMediaUrl?: string;
}

export const OTHERS_ENTRIES: OthersEntry[] = [];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&h=400&fit=crop";

function mapsUrl(e: OthersEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.fullAddress || e.address || ""}`)}`;
}

function getSocialMediaBtnProps(url: string) {
  if (!url) return null;
  const lower = url.toLowerCase();
  if (lower.includes("youtube.com") || lower.includes("youtu.be")) {
    return {
      label: "YouTube",
      bgClass: "bg-[#FF0000] hover:bg-[#cc0000]",
      icon: Youtube,
    };
  }
  if (lower.includes("instagram.com")) {
    return {
      label: "Instagram",
      bgClass: "bg-gradient-to-br from-pink-500 to-orange-400 hover:opacity-90",
      icon: Instagram,
    };
  }
  if (lower.includes("facebook.com")) {
    return {
      label: "Facebook",
      bgClass: "bg-[#1877F2] hover:bg-[#166fe5]",
      icon: Facebook,
    };
  }
  return {
    label: "Social Link",
    bgClass: "bg-gray-700 hover:bg-gray-800",
    icon: Share2,
  };
}

export function OthersCard({ entry, index }: { entry: OthersEntry; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full h-[180px] relative overflow-hidden bg-gray-100 flex items-center justify-center">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-1.5 pb-0.5 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-[10px] font-bold text-[#082f6d] leading-tight truncate">
            <a
              href={`/others/${slugify(entry.name || String(index))}`}
              className="hover:underline"
            >
              {entry.name}
            </a>
          </h3>
          {entry.description && (
            <p className="text-[9px] text-gray-500 font-medium leading-snug mt-0.5">
              {entry.description}
            </p>
          )}
          {(entry.fullAddress || entry.address) && (
            <div className="flex items-start gap-0.5 mt-0.5">
              <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
                {entry.fullAddress || entry.address}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        {entry.website && (
          <a
            href={entry.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-0.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <Globe className="w-2.5 h-2.5" /> Website
          </a>
        )}
        <SocialLinks item={{ ...entry, website: undefined }} variant="sleek" />
        {entry.socialMediaUrl && (() => {
          const btn = getSocialMediaBtnProps(entry.socialMediaUrl);
          if (!btn) return null;
          const Icon = btn.icon;
          return (
            <a
              href={entry.socialMediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center gap-0.5 text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 ${btn.bgClass}`}
            >
              <Icon className="w-2.5 h-2.5" /> {btn.label}
            </a>
          );
        })()}
        {entry.fullAddress && (
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-0.5 bg-[#51ba4f] hover:bg-[#45a943] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <ExternalLink className="w-2.5 h-2.5" /> View Location
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Others() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("others_listings", OTHERS_ENTRIES, (e, i) => slugify(e.name || String(i)));

  const filtered = mc.items.filter(
    (e) =>
      query.trim() === "" ||
      e.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Others"
        placeholder="Search other businesses…"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Other Businesses & Services
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              South Asian businesses in DFW that don't fit our main categories.
            </p>
            <AdminAddBar mc={mc} fields={OTHERS_FIELDS} label="Add Business" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={OTHERS_FIELDS} title="Business">
                    <OthersCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No businesses match your search.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
